import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { AddIcon, DownloadIcon, FitToPageIcon, MinusIcon } from '@common/utils/icons/icons';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ReportTitlePage from './ReportTitlePage';
import { getCurrentPageName, trackEvent } from 'services/analytics';
import { AnalyticsEvent, ReportType } from 'services/analytics-events';

const PdfWrapper: React.FC<{
  title: string;
  subtitle: string;
  reportType: ReportType;
  children: React.ReactNode;
}> = ({ title, subtitle, reportType, children }) => {
  const { t } = useTranslation();

  const printRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [scale, setScale] = useState(1);

  const printClickHandler = useReactToPrint({
    contentRef: printRef,
    documentTitle: title,
  });

  useEffect(() => {
    trackEvent(AnalyticsEvent.ReportPreviewed, {
      report_type: reportType,
      source_page: getCurrentPageName(),
    });
  }, [reportType]);

  const downloadClickHandler = () => {
    trackEvent(AnalyticsEvent.ReportDownloadRequested, {
      report_type: reportType,
      source_page: getCurrentPageName(),
    });
    printClickHandler();
  };

  useLayoutEffect(() => {
    fitToPageHandler();
  }, []);

  const fitToPageHandler = () => {
    setScale(
      containerRef.current == null
        ? 1
        : Math.min(
            containerRef.current.clientWidth / 816,
            containerRef.current.clientHeight / 1200,
          ),
    );
  };

  const increaseScaleClickHandler = (sign: 1 | -1) => {
    setScale((prevState) => prevState + 0.1 * sign);
  };

  return (
    <React.Fragment>
      <Stack
        direction="row"
        alignItems="center"
        p={2}
        justifyContent="space-between"
        sx={(theme) => ({ height: theme.spacing(7), backgroundColor: '#3C3C3C', width: '100%' })}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton sx={{ color: 'white' }} onClick={() => increaseScaleClickHandler(-1)}>
            <MinusIcon />
          </IconButton>
          <Typography sx={{ color: 'white' }}>{Math.ceil(scale * 10) * 10 + '%'}</Typography>
          <IconButton sx={{ color: 'white' }} onClick={() => increaseScaleClickHandler(1)}>
            <AddIcon />
          </IconButton>
          <Divider orientation="vertical" flexItem variant="middle" sx={{ borderColor: 'white' }} />
          <TooltipIconButton
            sx={{ color: 'white' }}
            icon={<FitToPageIcon />}
            onClick={fitToPageHandler}
            tooltip={t('button-fit-to-page')}
          />
        </Stack>
        <Button startIcon={<DownloadIcon />} sx={{ color: 'white' }} onClick={downloadClickHandler}>
          {t('button-download')}
        </Button>
      </Stack>
      <Stack
        ref={containerRef}
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: '#282828',
          alignItems: 'center',
          overflowY: 'auto',
          overflowX: 'hidden',
          '.page': {
            mb: 2,
          },
        }}
      >
        <Box
          sx={{
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
          }}
        >
          <Box
            ref={printRef}
            sx={{
              width: '210mm', // A4 width in millimeters (CSS generally understood)
            }}
          >
            <ReportTitlePage title={title} subtitle={subtitle} />
            {children}
          </Box>
        </Box>
      </Stack>
    </React.Fragment>
  );
};

export default PdfWrapper;
